import React from "react";
import { logClientError } from "@/lib/errorLogging";

const getFallbackCopy = () => {
  const isSpanish = typeof document !== "undefined" && document.documentElement.lang === "es";
  return {
    title: isSpanish ? "Algo salió mal" : "Something went wrong",
    description: isSpanish
      ? "La app encontró un error inesperado. Puedes recargar la página y volver a intentarlo."
      : "The app hit an unexpected error. You can reload the page and try again.",
    reload: isSpanish ? "Recargar página" : "Reload page",
    home: isSpanish ? "Ir al inicio" : "Go home",
  };
};

export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("AppErrorBoundary caught error:", error, errorInfo);
    logClientError({
      category: "error_boundary",
      severity: "error",
      title: error?.name || "App error",
      message: error?.message || "Se produjo un error",
      stack: [error?.stack, errorInfo?.componentStack].filter(Boolean).join("\n\n"),
      context: {
        componentStack: errorInfo?.componentStack || null,
      },
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const copy = getFallbackCopy();

    return (
      <div
        className="min-h-screen bg-background hero-gradient flex items-center justify-center px-6"
        data-testid="app-error-boundary"
      >
        <div className="w-full max-w-lg rounded-3xl border border-border bg-card/95 p-8 text-left shadow-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-primary" data-testid="app-error-boundary-label">
            Voxara Recovery
          </p>
          <h1 className="mt-4 text-4xl font-semibold text-foreground" data-testid="app-error-boundary-title">
            {copy.title}
          </h1>
          <p className="mt-4 text-base text-muted-foreground" data-testid="app-error-boundary-description">
            {copy.description}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
              data-testid="app-error-boundary-reload-button"
            >
              {copy.reload}
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="rounded-full border border-border px-5 py-3 text-sm font-semibold text-foreground"
              data-testid="app-error-boundary-home-button"
            >
              {copy.home}
            </button>
          </div>
        </div>
      </div>
    );
  }
}